/**
 * Os filtros da lista de leads moram na URL (doc 02, 1.3), no formato da API:
 * `tags=some id1,id2`, `kind=PERSON`, `additionalField=<id> contains valor` (pode repetir).
 * A mesma querystring vira visão e alvo das ações em massa.
 */

export type IdOperator = 'some' | 'every' | 'none';

export interface IdFilterValue {
  operator: IdOperator;
  ids: string[];
  /** "Sem tags", "Sem listas", "Sem atendente". */
  empty?: boolean;
}

export interface FieldFilterValue {
  fieldId: string;
  operator: 'eq' | 'contains' | 'not';
  value: string;
}

export interface LeadFilterState {
  search?: string;
  kind?: 'PERSON' | 'COMPANY';
  tags?: IdFilterValue;
  lists?: IdFilterValue;
  pipelines?: IdFilterValue;
  stages?: IdFilterValue;
  products?: IdFilterValue;
  attendants?: IdFilterValue;
  companies?: IdFilterValue;
  source?: string[];
  jobTitles?: string[];
  addressCountry?: string;
  addressState?: string;
  addressCity?: string;
  addressBlock?: string;
  additionalFields?: FieldFilterValue[];
  createdAtFrom?: string;
  createdAtTo?: string;
  lastPurchaseMin?: string;
  lastPurchaseMax?: string;
  businessesCountMin?: string;
  businessesCountMax?: string;
}

export const ID_FILTER_KEYS = [
  'tags',
  'lists',
  'pipelines',
  'stages',
  'products',
  'attendants',
  'companies',
] as const;

const TEXT_KEYS = [
  'search',
  'addressCountry',
  'addressState',
  'addressCity',
  'addressBlock',
  'createdAtFrom',
  'createdAtTo',
  'lastPurchaseMin',
  'lastPurchaseMax',
  'businessesCountMin',
  'businessesCountMax',
] as const;

const parseId = (raw: string | null): IdFilterValue | undefined => {
  if (!raw) return undefined;
  if (raw === 'empty') return { operator: 'some', ids: [], empty: true };
  const [head, ...rest] = raw.trim().split(/\s+/);
  const hasOperator = ['some', 'every', 'none'].includes(head);
  const ids = (hasOperator ? rest.join(' ') : raw).split(',').filter(Boolean);
  return { operator: (hasOperator ? head : 'some') as IdOperator, ids };
};

export function parseFilters(params: URLSearchParams): LeadFilterState {
  const state: LeadFilterState = {};
  for (const key of TEXT_KEYS) {
    const value = params.get(key);
    if (value) state[key] = value;
  }
  const kind = params.get('kind');
  if (kind === 'PERSON' || kind === 'COMPANY') state.kind = kind;
  for (const key of ID_FILTER_KEYS) {
    const value = parseId(params.get(key));
    if (value) state[key] = value;
  }
  const source = params.get('source');
  if (source) state.source = source.split(',').filter(Boolean);
  const titles = params.get('jobTitles');
  if (titles) state.jobTitles = titles.split(',').filter(Boolean);
  const fields = params.getAll('additionalField');
  if (fields.length) {
    state.additionalFields = fields
      .map((raw) => /^(\S+)\s+(\S+)\s+([\s\S]+)$/.exec(raw))
      .filter((match): match is RegExpExecArray => Boolean(match))
      .map(([, fieldId, operator, value]) => ({
        fieldId,
        operator: operator as FieldFilterValue['operator'],
        value,
      }));
  }
  return state;
}

/** Estado → querystring (sem paginação). Só as chaves preenchidas. */
export function serializeFilters(
  state: LeadFilterState,
  extra: Record<string, string> = {},
): string {
  const params = new URLSearchParams();
  for (const key of TEXT_KEYS) {
    const value = state[key];
    if (value) params.set(key, value);
  }
  if (state.kind) params.set('kind', state.kind);
  for (const key of ID_FILTER_KEYS) {
    const value = state[key];
    if (!value) continue;
    if (value.empty) params.set(key, 'empty');
    else if (value.ids.length) params.set(key, `${value.operator} ${value.ids.join(',')}`);
  }
  if (state.source?.length) params.set('source', state.source.join(','));
  if (state.jobTitles?.length) params.set('jobTitles', state.jobTitles.join(','));
  for (const field of state.additionalFields ?? []) {
    params.append('additionalField', `${field.fieldId} ${field.operator} ${field.value}`);
  }
  for (const [key, value] of Object.entries(extra)) if (value) params.set(key, value);
  return params.toString();
}

/** Quantos filtros (além da busca) estão ativos — o número do botão "Filtros". */
export function activeFilterCount(state: LeadFilterState): number {
  const { search: _search, ...rest } = state;
  return Object.values(rest).filter((value) =>
    Array.isArray(value) ? value.length > 0 : value !== undefined && value !== '',
  ).length;
}

export const OPERATOR_LABELS: Record<IdOperator, string> = {
  some: 'contém algum',
  every: 'contém todos',
  none: 'não contém nenhum',
};

export const FIELD_OPERATOR_LABELS: Record<FieldFilterValue['operator'], string> = {
  eq: 'Igual',
  contains: 'Contém',
  not: 'Diferente',
};

/** Atalhos de período dos filtros de data. */
export function periodShortcut(days: number | 'today'): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  if (days === 'today') from.setHours(0, 0, 0, 0);
  else from.setDate(from.getDate() - days);
  return { from: from.toISOString(), to: to.toISOString() };
}
